// @flow
import React from 'react';
import { connect } from 'react-redux';
import Button from '@ta/styleguide.button';
import { Icon } from '@ta/styleguide.icon';
import { Avatar } from '@ta/styleguide.avatar';
import {
  getRegistrationParams,
  getLoginUrl,
  getJoinUrl,
} from '@ta/brand.global-nav-action-profile';
import { localize } from '@ta/common.i18n';
import { Link } from '@ta/common.routing';
import trackEventAJAX, { ClickTracker } from '@ta/common.tracking';
import { featureIsEnabled } from '@ta/common.features';
import { Closer } from '@ta/overlays.managers';
import { ACTIONS as ONBOARDING_ACTIONS } from '@ta/social.onboarding-state';
import styles from './ProfileDataCell.less';

/** The data we need in order to display a user's information if they are logged in */
export type MemberData = {
  /** URL that links to this member's page */
  profileUrl?: string,
  /** The user's display name */
  userName?: string,
  /** Avatar thumbnail URL */
  avatarUrl?: string,
};

type ExternalProps = {|
  /** The data needed to display a logged in user's information. If null or undefined that means the user is not logged in */
  memberData: ?MemberData,
  openExplicitPreferences: boolean => boolean,
|};

type DispatchProps = {|
  /** Onboarding action function to set (i.e. if onboarding should be fired) */
  setFireOnboarding: typeof ONBOARDING_ACTIONS.setShouldFire,
|};

type Props = {|
  ...ExternalProps,
  ...DispatchProps,

  /** Current geo ID */
  geoId: number,
|};

const FLOW_ORIGIN_LOGIN = 'login';
const FLOW_ORIGIN_JOIN = 'join';
const ONBOARDING_ENTRY_POINT = 'MASTHEAD';

// MEMX-1003 tracking
const trackPromo = () => {
  trackEventAJAX({
    module: 'signup_promo_prompt',
    action: 'eligible_promo_test',
  });
};

/**
 * The component for the top cell of the sideBar menu. Either displays user info (avatar, name, "View Profile") if the user is logged in
 * or displays log in and join buttons if the user is not logged in
 */
const ProfileDataCell = (props: Props) => {
  const { memberData, openExplicitPreferences } = props;

  // If we have data for this member (if they are logged in), then display the data
  if (memberData !== null && typeof memberData !== 'undefined') {
    const profileImage = memberData.avatarUrl ? (
      <Avatar photoUrl={memberData.avatarUrl} className="ui_icon" />
    ) : (
      <Icon name="member" />
    );

    return (
      <div className={styles.profileDataCell}>
        <Link href={memberData.profileUrl}>
          <div className={styles.profileDataContainer}>
            {profileImage}
            <div className={styles.profileData}>
              <div className={styles.profileName}>{memberData.userName}</div>
              <div className={styles.viewProfileText}>{localize('ip_view_profile_1cf1')}</div>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  // Otherwise display the login and join buttons
  const regUrlParams = getRegistrationParams();
  const loginUrl = getLoginUrl(regUrlParams);
  const joinUrl = getJoinUrl(regUrlParams);
  const context = `Reg Form | ${regUrlParams.flowName}`;

  /** Bring up the onboarding flow after registration. */
  const onRegOverlaySuccess = regData => {
    // Will track and open Explicit Preferences flow when applicable
    if (openExplicitPreferences(regData.hasCreatedMember)) {
      return;
    }

    if (props.setFireOnboarding) {
      if (featureIsEnabled('post_signup_attr_promo_masthead_join') && regData.hasCreatedMember) {
        props.setFireOnboarding(true, 'SIGN_UP_AND_SAVE');
        trackPromo();
        return;
      }

      if (featureIsEnabled('post_signup_attr_promo_control') && regData.hasCreatedMember) {
        trackPromo();
      }

      // Use available info to make onboarding pop immediately if the user definitely has not seen it yet.
      const immediateLoad = regData && (regData.hasCreatedMember || !regData.hasAcceptedTermsOfUse);
      props.setFireOnboarding(immediateLoad, ONBOARDING_ENTRY_POINT);
    }
  };

  const openRegOverlay = (evt: SyntheticEvent<*>, flowOrigin: string) => {
    // eslint-disable-next-line no-restricted-globals
    window.require('ta/registration/RegOverlay').showForMobile(evt, evt.target, {
      flow: regUrlParams.flowName,
      pid: regUrlParams.loginPid,
      userRequestedForce: 'true',
      locationId: props.geoId,
      extraQueryParams: {
        flowOrigin,
        social: 'false',
      },
      onSuccess: regData => {
        onRegOverlaySuccess(regData);
      },
    });
    evt.preventDefault();
  };

  return (
    <Closer>
      {close => (
        <div className={styles.profileDataCellNotLoggedIn}>
          <ClickTracker action="prod_view" module="reg_trigger" context={context}>
            <Link
              href={loginUrl}
              onClick={evt => {
                close();
                openRegOverlay(evt, FLOW_ORIGIN_LOGIN);
              }}
            >
              <Button size="fullwidth" type="secondary">
                {/* Log in */}
                {localize('alerts_center_log_in')}
              </Button>
            </Link>
          </ClickTracker>
          <ClickTracker action="prod_view" module="reg_trigger" context={context}>
            <Link
              href={joinUrl}
              onClick={evt => {
                close();
                openRegOverlay(evt, FLOW_ORIGIN_JOIN);
              }}
            >
              <Button size="fullwidth" type="primary">
                {/* Join */}
                {!featureIsEnabled('daodao_unify_nav_links_title')
                  ? localize('utility_nav_join')
                  : localize('reg_and_join')}
              </Button>
            </Link>
          </ClickTracker>
        </div>
      )}
    </Closer>
  );
};

export default connect<Props, ExternalProps, _, _, _, _>(
  state => ({
    geoId: state.page.geoId,
  }),
  {
    setFireOnboarding: ONBOARDING_ACTIONS.setShouldFire,
  },
)(ProfileDataCell);
